import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import {
  VStack,
  Box,
  Heading,
  Text,
  HStack,
  Icon,
  Tabs,
  TabList,
  Tab,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { FaUsers, FaBolt, FaBoxOpen } from "react-icons/fa";
import OrdersTable from "./components/OrdersTable";
import { RefreshToolbar } from "./components/RefreshToolbar";
import { IoMdPricetag } from "react-icons/io";
import { ApiConfigProvider, setGlobalApiConfig } from "../../config/ApiConfig";
import { DispatchDashboardProps, GroupingMode, GroupingType } from "./types";
import { useCptSlabLevels } from "../ShipmentsDashboard/hooks/useCptSlabLevels";
import { CPT_SLAB_META, FASTEST_CPT_SLAB_LEVEL } from "./utils";
import { useGetMobileConfig } from "../../components/queryHooks";
import { MOBILE_CONFIG_KEYS } from "../../utils/constants";
import { isMobileConfigFlagEnabled } from "../../utils/util";
import { useDispatchDashboardStore } from "./store/useDispatchDashboardStore";
import { DispatchDashboardIcon } from "../../components/icons/DispatchDashboardIcon";

export default function DispatchDashboard(props: DispatchDashboardProps) {
  const {
    baseUrl,
    authToken,
    clientId,
    customFilters,
    riders,
    ridersLoading = false,
    maskDeliveryContactDetails,
    userName,
    enableDistanceTabs = false,
    refreshConfig,
    dashboardMode = "r2d",
    dashboardTitle,
  } = props;

  const resolvedDashboardTitle =
    dashboardTitle ||
    (dashboardMode === "dispatched" ? "Dispatch Dashboard" : "R2D Dashboard");

  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalOrders: 0,
    totalValues: 0,
    selectedOrders: 0,
    urgentOrders: 0,
    hldOrders: 0,
  });
  const [isApiConfigReady, setIsApiConfigReady] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(FASTEST_CPT_SLAB_LEVEL);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [grouping, setGrouping] = useState<GroupingType>(GroupingMode.SUPPLIER);
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(Date.now());
  const [collapseSignal, setCollapseSignal] = useState<number>(0);
  const refetchFnRef = useRef<(() => Promise<void>) | null>(null);
  const prevActiveTabRef = useRef<any>(null);
  const { levels } = useCptSlabLevels();
  const { data: mobileConfigs } = useGetMobileConfig(isApiConfigReady);
  const allowMultiCustomerShipment = isMobileConfigFlagEnabled(
    mobileConfigs?.[MOBILE_CONFIG_KEYS.FF_ONE_SHIPMENT_MULTIPLE_CUSTOMER_ORDER],
  );
  const setAllowMultiCustomerShipment = useDispatchDashboardStore(
    (state) => state.setAllowMultiCustomerShipment,
  );

  useLayoutEffect(() => {
    setAllowMultiCustomerShipment(allowMultiCustomerShipment);
  }, [allowMultiCustomerShipment, setAllowMultiCustomerShipment]);

  const sortedLevelKeys = useMemo(() => {
    if (!levels) return [];

    const priorityMap: Record<string, number> = {
      "1": 1, // Instant Delivery
      "2": 2, // Standard Delivery
      "-2": 3, // Self Pickup
      "-1": 4, // Others
    };

    return Object.keys(levels).sort((a, b) => {
      const priorityA = priorityMap[a] ?? 99;
      const priorityB = priorityMap[b] ?? 99;
      return priorityA - priorityB;
    });
  }, [levels]);

  const handleStatsUpdate = useCallback((newStats: any) => {
    setStats((prev) => ({ ...prev, ...newStats }));
  }, []);

  const handleGroupingChange = useCallback((value: string) => {
    setGrouping(value as GroupingType);
    setCollapseSignal(Date.now());
  }, []);

  const refreshIntervalMinutes = refreshConfig?.intervalMinutes || 10;

  const refreshIntervalMs = refreshConfig?.enabled
    ? refreshIntervalMinutes * 60 * 1000
    : undefined;

  const handleRefresh = useCallback(async () => {
    if (!refreshConfig?.enabled) {
      return;
    }

    setCollapseSignal(Date.now());

    if (refetchFnRef.current) {
      await refetchFnRef.current();
    }
  }, [refreshConfig?.enabled]);

  // Handle refresh on tab change (always refresh, regardless of refreshConfig)
  const handleTabChangeRefresh = useCallback(async () => {
    if (!enableDistanceTabs) {
      return;
    }

    setCollapseSignal(Date.now());

    if (refetchFnRef.current) {
      await refetchFnRef.current();
    }
  }, [enableDistanceTabs]);

  useEffect(() => {
    if (!refreshConfig?.enabled || !refreshIntervalMs) {
      return;
    }

    const interval = setInterval(() => {
      handleRefresh();
    }, refreshIntervalMs);

    return () => clearInterval(interval);
  }, [refreshConfig?.enabled, refreshIntervalMs, handleRefresh]);

  useEffect(() => {
    if (!enableDistanceTabs || !isApiConfigReady) {
      return;
    }

    if (prevActiveTabRef.current === null) {
      prevActiveTabRef.current = activeTab;
      return;
    }

    if (prevActiveTabRef.current !== activeTab) {
      prevActiveTabRef.current = activeTab;
      handleTabChangeRefresh();
    }
  }, [activeTab, enableDistanceTabs, isApiConfigReady, handleTabChangeRefresh]);

  useEffect(() => {
    setGlobalApiConfig({
      baseUrl,
      authToken,
      clientId,
      customFilters,
    });
    setIsApiConfigReady(true);
  }, [baseUrl, authToken, clientId, customFilters]);

  return (
    <ApiConfigProvider
      baseUrl={baseUrl}
      authToken={authToken}
      clientId={clientId}
      customFilters={customFilters}
    >
      <VStack h="full" spacing={0} align="stretch" bg="gray.50">
        <Box
          bg="linear-gradient(135deg, #dbeafe 0%, #93c5fd 50%, #60a5fa 100%)"
          borderBottom="1px"
          borderColor="gray.100"
          px={4}
          py={4}
          shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-100px"
            right="-100px"
            opacity={0.09}
            bg="linear-gradient(135deg, #64748b 0%, #475569 100%)"
            borderRadius="full"
            transform="translate(50px, -50px)"
          />
          <Box
            position="absolute"
            bottom="-80px"
            left="-80px"
            opacity={0.02}
            bg="linear-gradient(135deg, #475569 0%, #334155 100%)"
            borderRadius="full"
            transform="translate(-50px, 50px)"
          />

          <VStack spacing={6} align="stretch" position="relative" zIndex={1}>
            {/* Main Header */}
            <HStack justify="space-between" align="flex-start">
              <VStack align="start" spacing={3}>
                <HStack spacing={4} align="center" mt={"10px"} ml={"2rem"}>
                  <DispatchDashboardIcon size={20} color="#3182CE" />
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color="gray.800"
                      fontWeight="700"
                      letterSpacing="-0.025em"
                    >
                      {resolvedDashboardTitle}
                    </Heading>
                  </VStack>
                </HStack>
              </VStack>

              {/* Stats - Integrated Design */}
              <HStack
                spacing={8}
                display={{ base: "none", lg: "flex" }}
                align="center"
                mr={"2rem"}
              >
                {enableDistanceTabs && (
                  <VStack spacing={1} align="center">
                    <HStack spacing={2} align="center">
                      <span
                        role="img"
                        aria-label="ferrari"
                        style={{ fontSize: 18 }}
                      >
                        {CPT_SLAB_META[FASTEST_CPT_SLAB_LEVEL].icon}
                      </span>
                      <Text fontSize="sm" color="gray.600" fontWeight="600">
                        {levels?.[FASTEST_CPT_SLAB_LEVEL] ||
                          CPT_SLAB_META[FASTEST_CPT_SLAB_LEVEL].fallbackLabel}
                      </Text>
                    </HStack>
                    <Text fontSize="2xl" fontWeight="700" color="gray.800">
                      {stats.hldOrders}
                    </Text>
                  </VStack>
                )}
                {enableDistanceTabs && <Box w="1px" h="8" bg="gray.600" />}

                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Icon as={FaBoxOpen} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Orders
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.totalOrders}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Icon as={FaUsers} color="green.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Customers
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.totalCustomers}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Icon as={FaBolt} color="orange.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Urgent
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.urgentOrders}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center" width={"100px"}>
                  <HStack spacing={2} align="center">
                    <Icon as={IoMdPricetag} color="purple.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Value
                    </Text>
                  </HStack>
                  <Text fontSize="xl" fontWeight="700" color="gray.800">
                    ₹
                    {(isNaN(stats.totalValues)
                      ? 0
                      : stats.totalValues
                    ).toLocaleString()}
                  </Text>
                </VStack>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        {refreshConfig?.enabled && (
          <RefreshToolbar
            onManualRefresh={handleRefresh}
            isRefreshing={customersLoading}
            showCountdown={refreshConfig.enabled}
            dataUpdatedAt={dataUpdatedAt}
            refreshIntervalMinutes={refreshIntervalMinutes}
          />
        )}

        <Box flex={1} bg="gray.50" px={8} py={6} overflowY="auto">
          <HStack
            justify="space-between"
            align="center"
            flexWrap="nowrap"
            spacing={4}
            mb={4}
          >
           
              <Tabs
                onChange={(index) => {
                  setActiveTab(sortedLevelKeys[index]);
                }}
                variant="enclosed"
                colorScheme="blue"
              >
                 {enableDistanceTabs && (
                <TabList>
                  {sortedLevelKeys.map((level: string) => (
                    <Tab
                      key={level}
                      _selected={{
                        bg: "blue.50",
                        color: "blue.700",
                        borderColor: "blue.200",
                        fontWeight: "600",
                      }}
                      _hover={{ bg: "blue.50" }}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <span
                        role="img"
                        aria-label="ferrari"
                        style={{ fontSize: 18 }}
                      >
                        {CPT_SLAB_META[level]?.icon || "❓"}
                      </span>
                      {levels?.[level] ||
                        CPT_SLAB_META[level]?.fallbackLabel ||
                        "?"}
                    </Tab>
                  ))}
                </TabList>
              )}
            </Tabs>
             <HStack spacing={3} align="center" flexWrap="nowrap">
              <Text
                fontSize={"sm"}
                fontWeight="500"
                color="gray.800"
                whiteSpace="nowrap"
              >
                Group By:
              </Text>
              <RadioGroup value={grouping} onChange={handleGroupingChange}>
                <HStack spacing={4}>
                  <Radio value={GroupingMode.CUSTOMER} colorScheme="blue">
                    <Text fontWeight="500" color="gray.800" fontSize={"sm"}>
                      Customer
                    </Text>
                  </Radio>
                  <Radio value={GroupingMode.SUPPLIER} colorScheme="blue">
                    <Text fontWeight="500" color="gray.800" fontSize={"sm"}>
                      Supplier
                    </Text>
                  </Radio>
                </HStack>
              </RadioGroup>
            </HStack>
          </HStack>
          <OrdersTable
            onStatsUpdate={handleStatsUpdate}
            onRefetchReady={(refetch) => {
              refetchFnRef.current = refetch;
            }}
            onLoadingChange={setCustomersLoading}
            onDataUpdatedAtChange={setDataUpdatedAt}
            collapseSignal={collapseSignal}
            isApiConfigReady={isApiConfigReady}
            riders={riders}
            ridersLoading={ridersLoading}
            maskDeliveryContactDetails={maskDeliveryContactDetails}
            userName={userName}
            activeTab={enableDistanceTabs ? activeTab : undefined}
            enableDistanceTabs={enableDistanceTabs}
            grouping={grouping}
            allowMultiCustomerShipment={allowMultiCustomerShipment}
            dashboardMode={dashboardMode}
          />
        </Box>
      </VStack>
    </ApiConfigProvider>
  );
}
